import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import StrapiLandingHelmet from 'common/helmets/StrapiLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import StrapiWordmark from 'img/landing/strapi-wordmark.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const StrapiLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Strapi Case Study');
  }, [trackEvent]);

  return (
    <div className="landingStrapi landingCustomersPage">
      <StrapiLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Strapi uses Canny to interact with their community and close more deals" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/Ol_KNCvyA1A"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              Strapi empowers millions of people to create and share content anywhere. Like many
              startups, Strapi started as a student project in 2014 and grew into a 15-person CMS
              (content management system)&nbsp;platform.
            </p>
            <p>
              Aurélien Georget is the CPO (Chief Product Officer) at Strapi. Naturally, he does a
              bit of everything and primarily ensures that his team is set up for success. That
              includes getting the right tools – for the team and the&nbsp;customers.
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Strapi’s customers are a tight-knit community with opinions, ideas, and a desire to
              communicate with each other. That’s why Aurélien saw a need for a feedback management
              and prioritization&nbsp;tool.
            </p>
            <p>
              Before Canny, Strapi used a different approach. Even though they were collecting
              feedback, they were lacking the context to really understand the&nbsp;issues.
            </p>
            <p className="quote">
              &ldquo;We were using another solution. We didn't like it because it was lacking
              interaction with the community. So we started looking for a tool with a public roadmap
              where the community could also interact with us and also between each other. It was
              really important for us – to give them a place where they can chat about feature
              requests and what's missing in the&nbsp;product.&rdquo;
            </p>
            <p>
              Another issue: the previous tool didn’t have the API and the integrations Strapi
              needed. They had to resort to using spreadsheets to manage feedback, which definitely
              wasn’t the most efficient&nbsp;way.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>Strapi adopted Canny and saw immediate&nbsp;results.</p>
            <p className="quote">
              &ldquo;With Canny, it’s different. It really changed everything. Now we have threads
              and comments already attached to the feature. So it improves and makes our discovery
              process faster and&nbsp;smarter.&rdquo;
            </p>
            <p>
              Canny doesn’t just help the product team. Almost everyone at Strapi is using Canny
              every day (and seeing awesome&nbsp;results).
            </p>
            <ul>
              <li>Product managers – they check feature requests, feedback, and&nbsp;comments</li>
              <li>
                Sales team – they explain to prospects what’s coming and get them&nbsp;excited
              </li>
              <li>Customer success and support – provide visibility to current&nbsp;customers</li>
            </ul>
            <p className="quote">
              &ldquo;Canny even helps us win deals. When a prospect requests a feature, it’s easy
              for the sales team to log it in and follow up once it’s shipped. It changes everything
              for the customer – suddenly they have full visibility into the&nbsp;process.&rdquo;
            </p>
            <p>
              Aurélien and his team also really enjoy the simplicity of the design and the
              overall&nbsp;tool.
            </p>
            <p className="quote">
              &ldquo;It just works. The UX/UI is simple, people have no questions about using it.
              It’s the kind of product that I really&nbsp;love.&rdquo;
            </p>
            <p>
              The integrations really help Strapi make the most out of Canny. They use our Slack,
              Discord, and Github integrations. Aurélien even says that he was surprised by how many
              integrations Canny offers and how well they all&nbsp;work.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>Since adopting Canny, Strapi has doubled their number of&nbsp;interactions!</p>
            <p className="quote">
              &ldquo;We saw an increase of 100% in terms of comments. People now have more
              visibility, our team has more context, and overall our customer
              satisfaction&nbsp;grew.&rdquo;
            </p>
            <p>
              Aurélien sees the benefits all around – for the team, for the customers, and even
              for&nbsp;investors.
            </p>
            <p className="quote">
              &ldquo;We shortened the discovery time and reduced costs associated with that.
              Sometimes we don't even need to send out surveys anymore because we have enough people
              to interact&nbsp;with.&rdquo;
            </p>
            <p>Aurélien shared a story with&nbsp;us:</p>
            <p className="quote">
              &ldquo;Just this morning I was talking to a digital agency, and they told me: “We can
              invest in Strapi because we have the visibility of what’s going on and
              what’s&nbsp;coming.&rdquo;
            </p>
            <p>
              Overall, Strapi saw a huge boost in customer satisfaction thanks to Canny. Especially
              because they could compare it to a different product management software; Canny
              stood&nbsp;out.
            </p>
            <p>
              Aurelien stressed that Canny's simplicity, UI, features, and integrations just get the
              job&nbsp;done.
            </p>
            <p>
              We’re excited to see what’s next for Strapi! Team Canny will keep helping them reach
              new&nbsp;heights!
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Strapi Wordmark"
              className="strapiWordmark"
              src={StrapiWordmark}
            />
            Strapi is the leading open-source headless CMS. It’s 100% JavaScript and
            fully&nbsp;customizable.
            <a href="https://strapi.io/" rel="noopener" target="_blank">
              strapi.io
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>15</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2018</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2021</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>15,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default hot(StrapiLanding);
